import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../services/api';
import './Dashboard.css';

interface KPIData {
  clicks: number;
  conversions: number;
  cr: string;
  revenue: string;
  payout: string;
  profit: string;
  smartlink: string;
}

const Dashboard: React.FC = () => {
  const [kpiData, setKpiData] = useState<KPIData>({
    clicks: 0,
    conversions: 0,
    cr: '0%',
    revenue: '$0.00',
    payout: '$0.00',
    profit: '$0.00',
    smartlink: '$0.00'
  });

  const [advertiserReports, setAdvertiserReports] = useState<any[]>([]);
  const [offerReports, setOfferReports] = useState<any[]>([]);
  const [affiliateReports, setAffiliateReports] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'advertiser' | 'offer' | 'affiliate'>('advertiser');
  const [selectedAdvertiserId, setSelectedAdvertiserId] = useState<number | null>(null);
  const [selectedAdvertiserName, setSelectedAdvertiserName] = useState<string>('');
  const [selectedOfferId, setSelectedOfferId] = useState<number | null>(null);
  const [selectedOfferName, setSelectedOfferName] = useState<string>('');

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const response = await apiService.getDashboardAnalytics();
        setKpiData(response.data.kpi);
        setAdvertiserReports(response.data.advertiserReports);
      } catch (error: any) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const handleViewOffers = async (advertiserId: number, advertiserName: string) => {
    try {
      setLoading(true);
      const response = await apiService.getAdvertiserOffersReport(advertiserId);
      setOfferReports(response.data.offerReports);
      setSelectedAdvertiserId(advertiserId);
      setSelectedAdvertiserName(advertiserName);
      setViewMode('offer');
    } catch (error: any) {
      console.error('Failed to fetch advertiser offers:', error);
      alert('Failed to load offer report');
    } finally {
      setLoading(false);
    }
  };

  const handleViewAffiliates = async (offerId: number, offerName: string) => {
    try {
      setLoading(true);
      const response = await apiService.getOfferAffiliatesReport(offerId);
      setAffiliateReports(response.data.affiliateReports);
      setSelectedOfferId(offerId);
      setSelectedOfferName(offerName);
      setViewMode('affiliate');
    } catch (error: any) {
      console.error('Failed to fetch offer affiliates:', error);
      alert('Failed to load affiliate report');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToAdvertisers = () => {
    setViewMode('advertiser');
    setSelectedAdvertiserId(null);
    setSelectedAdvertiserName('');
    setSelectedOfferId(null);
    setSelectedOfferName('');
    setOfferReports([]);
    setAffiliateReports([]);
  };

  const handleBackToOffers = () => {
    setViewMode('offer');
    setSelectedOfferId(null);
    setSelectedOfferName('');
    setAffiliateReports([]);
  };

  const kpiCards = [
    { title: 'Clicks', value: kpiData.clicks.toLocaleString(), icon: '👆', color: '#3498db' },
    { title: 'Conversion', value: kpiData.conversions.toLocaleString(), icon: '✅', color: '#27ae60' },
    { title: 'CR%', value: kpiData.cr, icon: '📊', color: '#f39c12' },
    { title: 'Revenue', value: kpiData.revenue, icon: '💰', color: '#e74c3c' },
    { title: 'Payout', value: kpiData.payout, icon: '💸', color: '#9b59b6' },
    { title: 'Profit', value: kpiData.profit, icon: '📈', color: '#1abc9c' },
    { title: 'Smartlink', value: kpiData.smartlink, icon: '🔗', color: '#34495e' }
  ];

  if (loading) {
    return (
      <div className="dashboard">
        <div className="dashboard-header">
          <h1>Dashboard</h1>
        </div>
        <div className="dashboard-loading">
          Loading dashboard data...
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1>Dashboard</h1>
      </div>

      {/* KPI Cards - Only show in advertiser view */}
      {viewMode === 'advertiser' && (
        <div className="kpi-grid">
          {kpiCards.map((card, index) => (
            <div key={index} className="kpi-card" style={{ borderLeftColor: card.color }}>
              <div className="kpi-icon" style={{ color: card.color }}>
                {card.icon}
              </div>
              <div className="kpi-content">
                <div className="kpi-title">{card.title}</div>
                <div className="kpi-value" style={{ color: card.color }}>
                  <span className="trend-icon">↗</span>
                  {card.value}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Report Table */}
      <div className="report-section">
        <div className="report-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            {viewMode === 'offer' && (
              <button
                onClick={handleBackToAdvertisers}
                className="btn btn-secondary"
                style={{ padding: '8px 16px' }}
              >
                ← Back
              </button>
            )}
            {viewMode === 'affiliate' && (
              <button
                onClick={handleBackToOffers}
                className="btn btn-secondary"
                style={{ padding: '8px 16px' }}
              >
                ← Back
              </button>
            )}
            <h2>
              {viewMode === 'advertiser'
                ? 'Advertiser Report'
                : viewMode === 'offer'
                ? 'Advertiser Wise Offer Wise Report'
                : 'Offer Wise Affiliate Report'}
            </h2>
          </div>
          <div className="report-controls">
            <div className="export-buttons">
              <button className="btn btn-success">Copy</button>
              <button className="btn btn-info">CSV</button>
              <button className="btn btn-warning">Excel</button>
              <button className="btn btn-danger">PDF</button>
              <button className="btn btn-secondary">Print</button>
            </div>
            <div className="search-box">
              <input type="text" placeholder="Search..." className="search-input" />
            </div>
          </div>
        </div>

        <div className="table-container">
          <table className="report-table">
            <thead>
              <tr>
                {viewMode === 'advertiser' ? (
                  <>
                    <th>Date</th>
                    <th>Advertiser</th>
                    <th>Clicks</th>
                    <th>Conversions</th>
                    <th>CR</th>
                    <th>Revenue</th>
                    <th>Payout</th>
                    <th>Profit</th>
                    <th>Action</th>
                  </>
                ) : viewMode === 'offer' ? (
                  <>
                    <th>Date</th>
                    <th>Advertiser</th>
                    <th>Offer ID</th>
                    <th>Offer</th>
                    <th>Clicks</th>
                    <th>Conversions</th>
                    <th>CR</th>
                  </>
                ) : (
                  <>
                    <th>Date</th>
                    <th>Advertiser</th>
                    <th>Offer ID</th>
                    <th>Offer</th>
                    <th>Affiliate</th>
                    <th>Clicks</th>
                    <th>Conversions</th>
                    <th>CR</th>
                    <th>Revenue</th>
                    <th>Payout</th>
                    <th>Profit</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {viewMode === 'advertiser' ? (
                advertiserReports.map((report, index) => (
                  <tr key={index}>
                    <td>{report.date}</td>
                    <td>{report.advertiser}</td>
                    <td>{report.clicks.toLocaleString()}</td>
                    <td>{report.conversions}</td>
                    <td>{report.cr}</td>
                    <td>{report.revenue}</td>
                    <td>{report.payout}</td>
                    <td>{report.profit}</td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <a
                          href="#"
                          className="dashboard-link"
                          onClick={(e) => {
                            e.preventDefault();
                            handleViewOffers(report.advertiser_id, report.advertiser);
                          }}
                        >
                          Offers
                        </a>
                        <span className="dashboard-link dashboard-link-muted">
                          Affiliates
                        </span>
                      </div>
                    </td>
                  </tr>
                ))
              ) : viewMode === 'offer' ? (
                offerReports.map((report, index) => (
                  <tr
                    key={index}
                    onClick={() => handleViewAffiliates(report.offer_id, report.offer_name)}
                    className="dashboard-row-clickable"
                  >
                    <td>{report.date}</td>
                    <td>{report.advertiser}</td>
                    <td>{report.offer_id}</td>
                    <td>
                      <Link
                        to={`/offers/view/${report.offer_id}`}
                        className="dashboard-link"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {report.offer_name}
                      </Link>
                    </td>
                    <td>{report.clicks.toLocaleString()}</td>
                    <td>{report.conversions}</td>
                    <td>{report.cr}</td>
                  </tr>
                ))
              ) : (
                affiliateReports.map((report, index) => (
                  <tr key={index}>
                    <td>{report.date}</td>
                    <td>{report.advertiser}</td>
                    <td>{report.offer_id}</td>
                    <td>{report.offer_name}</td>
                    <td>
                      <Link
                        to={`/publishers/${report.affiliate_id}`}
                        className="dashboard-link"
                      >
                        {report.affiliate}
                      </Link>
                    </td>
                    <td>{report.clicks.toLocaleString()}</td>
                    <td>{report.conversions}</td>
                    <td>{report.cr}</td>
                    <td>{report.revenue}</td>
                    <td>{report.payout}</td>
                    <td>{report.profit}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="table-footer">
          <div className="table-info">
            Showing 1 to 10 of 14 entries
          </div>
          <div className="pagination">
            <button className="page-btn">Previous</button>
            <button className="page-btn active">1</button>
            <button className="page-btn">2</button>
            <button className="page-btn">Next</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
